import type {
  BackendDataMaskingActiveRunDto,
  BackendDataMaskingMigrationSetDto,
  BackendDataMaskingRunPageDto,
  BackendDataMaskingRunProvenanceDto,
  BackendDataMaskingRunSummaryDto,
  BackendDataMaskingStageManifestDto,
  DataMaskingActiveRun,
  DataMaskingMigrationSet,
  DataMaskingRunPage,
  DataMaskingRunProvenance,
  DataMaskingRunSummary,
  DataMaskingStageManifest,
} from '@/features/data-masking/data-masking.def.ts';

const toOptionalString = (value?: string | null) => value ?? undefined;

const transformMigrationSet = (
  dto: BackendDataMaskingMigrationSetDto,
): DataMaskingMigrationSet => ({
  migrationIds: [...dto.migrationIds],
  fingerprint: dto.fingerprint,
});

export const transformDataMaskingRunProvenance = (
  dto: BackendDataMaskingRunProvenanceDto,
): DataMaskingRunProvenance => ({
  profileVersion: dto.profileVersion,
  profileFingerprint: toOptionalString(dto.profileFingerprint),
  // Copied field by field rather than aliased, so the mapped model never shares the backend object.
  numericParameters: dto.numericParameters
    ? {
        jitterFraction: dto.numericParameters.jitterFraction,
        thresholdFloor: dto.numericParameters.thresholdFloor,
      }
    : undefined,
  maskingKeyVersion: dto.maskingKeyVersion,
  aliasAlgorithmVersion: dto.aliasAlgorithmVersion,
  aliasTemplateVersion: dto.aliasTemplateVersion,
  bogusPackageVersion: dto.bogusPackageVersion,
  aliasLocale: dto.aliasLocale,
  sourceMigrationSet: transformMigrationSet(dto.sourceMigrationSet),
  targetMigrationSet: transformMigrationSet(dto.targetMigrationSet),
});

export const transformDataMaskingStageManifest = (
  dto: BackendDataMaskingStageManifestDto,
): DataMaskingStageManifest => ({
  rowCounts: { ...dto.rowCounts },
  displayIdSequenceLastValue: dto.displayIdSequenceLastValue,
  displayIdSequenceIsCalled: dto.displayIdSequenceIsCalled,
});

export const transformDataMaskingRunSummary = (
  dto: BackendDataMaskingRunSummaryDto,
): DataMaskingRunSummary => ({
  runId: dto.runId,
  status: dto.status,
  profileId: dto.profileId,
  requestedByUsername: dto.requestedByUsername,
  requestedAt: dto.requestedAt,
  startedAt: toOptionalString(dto.startedAt),
  confirmedAt: toOptionalString(dto.confirmedAt),
  completedAt: toOptionalString(dto.completedAt),
  failureCode: toOptionalString(dto.failureCode),
  activeSnapshotId: toOptionalString(dto.activeSnapshotId),
  provenance: dto.provenance ? transformDataMaskingRunProvenance(dto.provenance) : undefined,
  stageManifest: dto.stageManifest
    ? transformDataMaskingStageManifest(dto.stageManifest)
    : undefined,
});

export const transformDataMaskingRunPage = (
  dto: BackendDataMaskingRunPageDto,
): DataMaskingRunPage => ({
  items: dto.items.map(transformDataMaskingRunSummary),
  totalCount: dto.totalCount,
  page: dto.page,
  pageSize: dto.pageSize,
});

export const transformDataMaskingActiveRun = (
  dto: BackendDataMaskingActiveRunDto,
): DataMaskingActiveRun => ({
  hasActiveRun: dto.hasActiveRun,
  run: dto.run ? transformDataMaskingRunSummary(dto.run) : undefined,
});
